import React, { ImgHTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';

type LazyImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  // omit optional src to replace it with our required src
  src: string;
  dataOriginal?: string;
  intersectionRootMargin?: string;
  retry?: number;
  delay?: number;
};

// Preloads the image off-screen and resolves true/false depending on whether it loaded.
// This leverages the browser HTTP cache for the subsequent <img> swap)
const preloadImage = (url: string, signal?: AbortSignal): Promise<boolean> => {
  return new Promise((resolve) => {
    const image = new Image();
    let resolved = false;

    const finish = (ok: boolean) => {
      if (resolved) {
        return;
      }
      resolved = true;
      image.onload = null;
      image.onerror = null;
      resolve(ok);
    };

    const onAbort = () => finish(false);
    signal?.addEventListener('abort', onAbort, { once: true });

    image.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      finish(true);
    };
    image.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      finish(false);
    };

    image.src = url;
  });
};

export const LazyImg = ({
  src,
  dataOriginal,
  intersectionRootMargin = '200px 0px',
  loading = 'lazy',
  retry,
  delay = 3000,
  onError,
  ...rest
}: LazyImgProps) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [currentSrc, setCurrentSrc] = useState(src);
  const attemptsRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController>();

  if (retry > 1) {
    // console.log('will retry', retry, dataOriginal)
  }
  useEffect(() => {
    setCurrentSrc(src);
    attemptsRef.current = 0;
  }, [src]);

  useEffect(() => {
    return () => {
      clearTimeout(retryTimeoutRef.current);
      abortRef.current?.abort();
    };
  }, []);

  // Preloads `original`. On failure, retries up to `retry` times (separated by
  // `delay` ms) before giving up and leaving the placeholder in place.
  const attemptLoad = useCallback(
    (original: string) => {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
      abortRef.current = controller;

      preloadImage(original, controller?.signal).then((ok) => {
        if (controller?.signal.aborted) {
          return;
        }
        if (ok) {
          attemptsRef.current = 0;
          setCurrentSrc(original);
          return;
        }
        if (retry != null && attemptsRef.current < retry) {
          attemptsRef.current += 1;
          // console.log('retrying fetching', original)
          retryTimeoutRef.current = setTimeout(() => attemptLoad(original), delay);
        }
      });
    },
    [retry, delay],
  );

  useEffect(() => {
    const original = dataOriginal?.trim();
    if (!original || original === src) {
      return undefined;
    }

    const element = imageRef.current;

    if (!element || typeof IntersectionObserver === 'undefined') {
      attemptLoad(original);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          attemptLoad(original);
        }
      },
      { root: null, rootMargin: intersectionRootMargin, threshold: 0.01 },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [dataOriginal, intersectionRootMargin, src, attemptLoad]);

  // If the swapped-in original fails to actually render (e.g. removed between
  // preload and paint), retry up to `retry` times (separated by `delay` ms)
  // before reverting to the placeholder instead of showing a broken image icon.
  const handleError: ImgHTMLAttributes<HTMLImageElement>['onError'] = (event) => {
    const original = dataOriginal?.trim();
    // console.warn('handleError loading', event)
    if (original && currentSrc === original && retry != null && attemptsRef.current < retry) {
      attemptsRef.current += 1;
      retryTimeoutRef.current = setTimeout(() => attemptLoad(original), delay);
      return;
    }

    if (currentSrc !== src) {
      setCurrentSrc(src);
    }
    onError?.(event);
  };

  return <img ref={imageRef} {...rest} loading={loading} src={currentSrc} onError={handleError} />;
};
